<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue';
import type { Feedback } from '@/shared/apollo-api/__generated__/types';
import { dateToLocale } from '../../../helpers/date';
import StarRating from '../../common/star-rating.vue';
import PhotoItem from './photo-item.vue';
import ProductTabsReviewsPhotoSlider from './product-tabs-reviews-photo-slider.vue';
import { MMText } from '@mm-frontend/mm-web-ui-kit';

interface Props {
  feedback: Feedback;
  itsFeedbackMobile?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  itsFeedbackMobile: false,
});

const emit = defineEmits<{
  'event-show-review': [];
}>();

const observer = ref<IntersectionObserver | null>(null);
const instance = getCurrentInstance();

const skuText = computed(() => {
  return props.feedback.sku.characteristicValues
    .map((char: any) => `${char.characteristic.title}: ${char.title}`)
    .join(' • ');
});

function eventShow() {
  emit('event-show-review');
}

function trueDate(data: string) {
  const published = new Date(data);

  if (published.getFullYear() === new Date().getFullYear()) {
    return dateToLocale(published, { day: 'numeric', month: 'long' });
  } else {
    return dateToLocale(published, { day: 'numeric', month: 'long', year: 'numeric' }).slice(0, -3);
  }
}

onMounted(() => {
  observer.value = new IntersectionObserver(([entry]) => {
    if (entry && entry.isIntersecting) {
      eventShow();
    }
  });
  if (instance?.proxy?.$el) {
    observer.value.observe(instance.proxy.$el);
  }
});

onUnmounted(() => {
  observer.value?.disconnect();
});
</script>

<template>
  <div class="wrap">
    <div class="top-heading">
      <div class="top-heading-info">
        <div class="top-heading-stars">
          <star-rating :rating="feedback.rating" />
        </div>
        <span class="name">
          {{ feedback.customerName }}{{ feedback.anonymous ? ' (Анонимный отзыв)' : '' }}
        </span>
      </div>
      <span class="date">{{ trueDate(feedback.datePublished) }}</span>
    </div>
    <MMText font="Body L/Regular" color="tertiary" class="sku">{{ skuText }}</MMText>
    <MMText font="Body L/Regular Long">
      {{ feedback.content }}
    </MMText>
    <div v-if="feedback.photos && feedback.photos.length" class="photos">
      <!-- TODO: Заменить на фото слайдер -->
      <!--      <template v-for="photo in feedback.photos">-->
      <!--        <PhotoItem :key="photo.key" :photo="photo" />-->
      <!--      </template>-->
      <product-tabs-reviews-photo-slider
        :feedbackWithPhotos="[feedback]"
        :feedbackWithPhotosLoading="false"
        :itsFeedbackMobile="itsFeedbackMobile"
      />
    </div>
    <template v-if="feedback.reply && feedback.reply.length">
      <div v-for="reply in feedback.reply" :key="reply.id" class="answer">
        <div class="answer-header">
          <img class="answer-logo" :src="reply.shop.avatar.high" alt="logo" />
          <div class="answer-main">
            <div class="answer-head">
              <div class="answer-name name">Ответ от продавца</div>
              <div class="answer-date date">{{ trueDate(reply.dateCreated) }}</div>
            </div>
            <div class="answer-text content">{{ reply.content }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="stylus">
.answer
  margin-top 24px
  &-header
    display flex
  &-logo
    height 48px
    width 48px
  &-main
    padding 15px
    width 100%
    margin-left 10px
    border 1px var(--border-subtle, rgba(54, 54, 64, 0.08)) solid
    border-radius 8px
  &-head
    margin-bottom 8px
    display flex
    justify-content space-between
    align-items center
    flex-wrap wrap
  &-header
    img
      border-radius 8px

.top-heading
  display flex
  align-items start
  justify-content space-between
  &-stars
    margin-right 10px
  &-info
    display flex
    align-items center
    +Media(Mobile, Tablet)
      flex-direction column
    flex-wrap wrap
    gap 5px

.name
  display block
  color: var(--gray-gray-dk-40, #141415);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.07px;

.sku
  margin-top 16px
  margin-bottom 12px
  +Media(Mobile, Tablet)
    margin 8px auto

$gap = 12px
$height = 145px

.photos
  display flex
  flex-direction row
  overflow-y hidden
  overflow-x scroll
  align-items flex-start
  gap $gap
  height $height
  margin 15px auto
  &::-webkit-scrollbar {
    display: none; /* for Chrome, Safari, and Opera */
  }

.date
  display block
  color: var(--gray-60, #8B8E99);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.07px;
</style>
